import './cart-operations.js';

/**
 * Cart line controls — shared between cart-drawer and cart page.
 *
 * Binds to any [data-cart-items] container in the DOM. Handles:
 *   - Quantity stepper (+/−)
 *   - Remove line item
 *   - Section refresh via Shopify Section Render API after cart mutation
 *
 * After a cart mutation, the cart-drawer and cart sections receive
 * fresh server-rendered HTML; this re-binds controls and dispatches
 * a `cart:updated` DOM event so other consumers (drawer open/close,
 * free-shipping-variant updater, etc.) can react.
 *
 * Tasks 6 + 7 fill in onClick + applyUpdate. Task 5 is the scaffold.
 */

const SURFACE_SELECTOR = '[data-cart-page], [data-cart-drawer]';
const DEFAULT_MESSAGES = {
  updateError: 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
  updateSuccess: 'Menge für __TITLE__ auf __QUANTITY__ aktualisiert.',
  removeSuccess: '__TITLE__ wurde aus dem Warenkorb entfernt.',
};
function operations() {
  if (!window.CartOperations) window.CartOperations = globalThis.createCartOperations();
  return window.CartOperations;
}
let cartLineMutationPending = false;

function lockQuantityControls() {
  const locked = operations().hasPending() || operations().isBlocked();
  document.querySelectorAll('[data-cart-items]').forEach((container) => {
    const ready = !!container.__cartLineControls;
    container.querySelectorAll('[data-quantity-value]').forEach((input) => { input.readOnly = !ready || locked; });
    container.querySelectorAll('[data-quantity-plus], [data-quantity-minus], [data-remove-item]').forEach((button) => {
      const quantity = Number(button.closest('[data-cart-item]')?.querySelector('[data-quantity-value]')?.defaultValue);
      button.disabled = !ready || locked || (button.matches('[data-quantity-minus]') && quantity <= 1);
    });
    container.querySelectorAll('[data-cart-controls-unavailable]').forEach((message) => { message.hidden = ready; });
  });
  if (!locked && document.__cartAfterIdleFocus) {
    const restore = document.__cartAfterIdleFocus;
    document.__cartAfterIdleFocus = null;
    restore();
  }
  if (operations().isBlocked()) {
    document.querySelectorAll('[data-cart-update-alert]').forEach((alert) => {
      alert.textContent = (window.CartMessages?.uncertain || 'Die Aktualisierung ist unklar. Bitte laden Sie den Warenkorb vor weiteren Änderungen neu.') + ' ';
      const reload = document.createElement('a');
      reload.href = window.location.href;
      reload.textContent = window.CartMessages?.reload || 'Warenkorb neu laden';
      alert.append(reload);
    });
  }
}

class CartUpdateError extends Error {
  constructor(message, code = 'update') {
    super(message);
    this.name = 'CartUpdateError';
    this.code = code;
  }
}

function surfaceKind(surface) {
  if (!surface) return null;
  return surface.hasAttribute('data-cart-page') ? 'page' : 'drawer';
}

function findLineByKey(surface, lineKey) {
  if (!surface || !lineKey) return null;
  return Array.from(surface.querySelectorAll('[data-cart-item]'))
    .find((line) => line.dataset.lineKey === lineKey) || null;
}

function findLineForIntent(surface, intent) {
  const keyedLine = findLineByKey(surface, intent.lineKey);
  if (keyedLine) return keyedLine;
  if (!intent.identity) return null;
  const matches = Array.from(surface?.querySelectorAll('[data-cart-item]') || [])
    .filter((line) => line.dataset.lineIdentity === intent.identity);
  return matches.length === 1 ? matches[0] : null;
}

function parseErrorValue(value) {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(parseErrorValue).filter(Boolean).join(' ');
  if (value && typeof value === 'object') {
    return Object.values(value).map(parseErrorValue).filter(Boolean).join(' ');
  }
  return '';
}

function errorMessageFrom(data, fallback = DEFAULT_MESSAGES.updateError) {
  if (!data || typeof data !== 'object') return fallback;
  return parseErrorValue(data.description)
    || parseErrorValue(data.message)
    || parseErrorValue(data.errors)
    || fallback;
}

function jsonCartUrl(url) {
  const value = String(url || '');
  const match = value.match(/^([^?#]*)([?#].*)?$/);
  if (!match) return value;
  const path = match[1].replace(/\/$/, '');
  return `${/\.js$/.test(path) ? path : `${path}.js`}${match[2] || ''}`;
}

function isValidCartPayload(data) {
  return !!data
    && typeof data === 'object'
    && Number.isInteger(data.item_count)
    && data.item_count >= 0
    && typeof data.total_price === 'number'
    && Array.isArray(data.items)
    && !!data.sections
    && typeof data.sections === 'object';
}

export class CartLineControls {
  constructor(container) {
    if (!container) throw new Error('CartLineControls requires a container');
    if (container.__cartLineControls) return container.__cartLineControls;
    this.container = container;
    Object.defineProperty(container, '__cartLineControls', {
      value: this,
      configurable: true,
    });
    this.bind();
    if (!document.__cartOperationsBound) {
      document.__cartOperationsBound = true;
      document.addEventListener('cart:operations-changed', lockQuantityControls);
    }
    lockQuantityControls();
  }

  static commitFocusedQuantity() {
    if (operations().hasPending() || operations().isBlocked()) return;
    const inputs = Array.from(document.querySelectorAll('input[data-quantity-value]'));
    const input = inputs.find((field) => field.value !== field.defaultValue);
    if (input) input.closest('[data-cart-items]')?.__cartLineControls?.onQuantityChange({ target: input });
  }

  static hasPendingUpdates() {
    return operations().hasPending() || cartLineMutationPending;
  }

  static updateFailureCount() {
    return operations().failureCount();
  }

  static async waitForPendingUpdates() {
    return operations().waitForIdle();
  }

  bind() {
    this.boundOnClick = (event) => this.onClick(event);
    this.container.addEventListener('click', this.boundOnClick);
    this.container.addEventListener('input', (event) => {
      if (!event.target.matches('input[data-quantity-value]')) return;
      const row = event.target.closest('[data-cart-item]');
      if (!document.__cartQuantityDrafts) document.__cartQuantityDrafts = new Map();
      document.__cartQuantityDrafts.set(row.dataset.lineKey, { value: event.target.value, identity: row.dataset.lineIdentity });
    });
    this.container.addEventListener('change', (event) => this.onQuantityChange(event));
    this.container.addEventListener('keydown', (event) => {
      if (!event.target.matches('input[data-quantity-value]')) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onQuantityChange(event);
      } else if (event.key === 'Escape') {
        event.target.value = event.target.defaultValue;
        document.__cartQuantityDrafts?.delete(event.target.closest('[data-cart-item]')?.dataset.lineKey);
      }
    });
  }

  onQuantityChange(event) {
    const input = event.target;
    if (!input.matches('input[data-quantity-value]')) return;
    const row = input.closest('[data-cart-item]');
    if (!row?.dataset.lineKey || CartLineControls.hasPendingUpdates() || operations().isBlocked()) return;
    const quantity = Number(input.value);
    if (!input.value.trim() || !Number.isSafeInteger(quantity) || quantity < 1) {
      input.value = input.defaultValue;
      document.__cartQuantityDrafts?.delete(row.dataset.lineKey);
      return;
    }
    if (quantity === Number(input.defaultValue)) { document.__cartQuantityDrafts?.delete(row.dataset.lineKey); return; }
    void this.update(row.dataset.lineKey, quantity, row, input, 'input');
  }

  onClick(event) {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target
      : null;
    if (!target) return;
    const minus = target.closest('[data-quantity-minus]');
    const plus = target.closest('[data-quantity-plus]');
    const remove = target.closest('[data-remove-item]');
    const trigger = minus || plus || remove;
    if (!trigger || !this.container.contains(trigger)) return;

    const lineEl = trigger.closest('[data-cart-item]');
    if (
      !lineEl
      || lineEl.getAttribute('aria-busy') === 'true'
      || cartLineMutationPending
      || operations().hasPending()
      || operations().isBlocked()
    ) return;
    const lineKey = lineEl.dataset.lineKey;
    const quantityEl = lineEl.querySelector('[data-quantity-value]');
    const currentQuantity = Number.parseInt(quantityEl?.value || quantityEl?.textContent || '', 10);
    if (!lineKey || !Number.isInteger(currentQuantity)) return;

    event.preventDefault();
    if (minus) {
      if (minus.disabled || currentQuantity <= 1) return;
      void this.update(lineKey, currentQuantity - 1, lineEl, minus, 'minus');
      return;
    }
    if (plus) {
      void this.update(lineKey, currentQuantity + 1, lineEl, plus, 'plus');
      return;
    }
    void this.update(lineKey, 0, lineEl, remove, 'remove');
  }

  captureIntent(lineKey, quantity, lineEl, trigger, action) {
    const rows = Array.from(this.container.querySelectorAll('[data-cart-item]'));
    const rowIndex = rows.indexOf(lineEl);
    const sourceSurface = lineEl.closest(SURFACE_SELECTOR);
    return {
      action,
      quantity,
      lineKey,
      variantId: lineEl.dataset.variantId || null,
      identity: lineEl.dataset.lineIdentity || null,
      rowIndex,
      lineEl,
      trigger,
      title: lineEl.querySelector('.cart-line__title')?.textContent?.trim() || 'Artikel',
      nextLineKey: rows[rowIndex + 1]?.dataset.lineKey || null,
      previousLineKey: rows[rowIndex - 1]?.dataset.lineKey || null,
      sourceKind: surfaceKind(sourceSurface),
      sourceSectionId: sourceSurface?.dataset.cartSectionId || null,
      sourceSurface,
      focusCaptured: false,
      shouldRestoreFocus: false,
    };
  }

  captureFocusOwnership(intent) {
    if (intent.focusCaptured) return;
    intent.focusCaptured = true;
    const active = document.activeElement;
    intent.shouldRestoreFocus = !!intent.trigger
      && intent.trigger.isConnected
      && (active === intent.trigger || intent.trigger.contains(active));
  }

  currentSurface(intent) {
    const selector = intent.sourceKind === 'page' ? '[data-cart-page]' : '[data-cart-drawer]';
    const surfaces = Array.from(document.querySelectorAll(selector));
    return surfaces.find((surface) => surface.dataset.cartSectionId === intent.sourceSectionId)
      || surfaces[0]
      || (intent.sourceSurface?.isConnected ? intent.sourceSurface : null);
  }

  getRequestConfig(intent) {
    const source = this.currentSurface(intent) || intent.sourceSurface;
    const sectionIds = Array.from(document.querySelectorAll(SURFACE_SELECTOR))
      .map((surface) => (surface.dataset.cartSectionId || '').trim())
      .filter((id, index, ids) => id && ids.indexOf(id) === index);
    if (!source || sectionIds.length === 0) {
      throw new CartUpdateError(DEFAULT_MESSAGES.updateError, 'configuration');
    }

    const localeRoot = window.Shopify?.routes?.root || '/';
    const normalizedRoot = localeRoot.endsWith('/') ? localeRoot : `${localeRoot}/`;
    const pathname = window.location?.pathname || '/cart';
    const search = window.location?.search || '';
    return {
      changeUrl: jsonCartUrl(source.dataset.cartChangeUrl || `${normalizedRoot}cart/change.js`),
      updateUrl: jsonCartUrl(source.dataset.cartUpdateUrl || `${normalizedRoot}cart/update.js`),
      sectionIds,
      sectionsUrl: `${pathname}${search}`,
    };
  }

  messages(intent) {
    const surface = this.currentSurface(intent) || intent.sourceSurface;
    const status = surface?.querySelector('[data-cart-update-status]');
    return {
      updateError: status?.dataset.textUpdateError || DEFAULT_MESSAGES.updateError,
      updateSuccess: status?.dataset.textUpdateSuccess || DEFAULT_MESSAGES.updateSuccess,
      removeSuccess: status?.dataset.textRemoveSuccess || DEFAULT_MESSAGES.removeSuccess,
    };
  }

  clearFeedback(intent) {
    const currentSurfaces = Array.from(document.querySelectorAll(SURFACE_SELECTOR));
    const fallbackSurface = this.currentSurface(intent) || intent.sourceSurface;
    const surfaces = currentSurfaces.length > 0 ? currentSurfaces : [fallbackSurface].filter(Boolean);
    surfaces.forEach((surface) => {
      const alert = surface.querySelector('[data-cart-update-alert]');
      const status = surface.querySelector('[data-cart-update-status]');
      if (alert) alert.textContent = '';
      if (status) status.textContent = '';
    });
  }

  showError(intent, message) {
    const surface = this.currentSurface(intent) || intent.sourceSurface;
    const alert = surface?.querySelector('[data-cart-update-alert]');
    if (!alert) return;
    alert.textContent = '';
    alert.textContent = message || this.messages(intent).updateError;
  }

  formatMessage(template, intent, quantity) {
    return template
      .replaceAll('__TITLE__', intent.title)
      .replaceAll('__QUANTITY__', String(quantity));
  }

  announceSuccess(intent) {
    const surface = this.currentSurface(intent) || intent.sourceSurface;
    const status = surface?.querySelector('[data-cart-update-status]');
    if (!status) return;
    const messages = this.messages(intent);
    let message;
    if (intent.action === 'remove' || intent.quantity === 0) {
      message = this.formatMessage(messages.removeSuccess, intent, 0);
    } else {
      const row = findLineForIntent(surface, intent);
      const actualQuantity = Number.parseInt(
        row?.querySelector('[data-quantity-value]')?.value
          || row?.querySelector('[data-quantity-value]')?.textContent || String(intent.quantity),
        10,
      );
      message = this.formatMessage(messages.updateSuccess, intent, actualQuantity);
    }
    status.textContent = '';
    status.textContent = message;
  }

  focusElement(element) {
    if (!element || !element.isConnected || typeof element.focus !== 'function') return false;
    if (element.matches(':disabled, [aria-disabled="true"]')) return false;
    element.focus({ preventScroll: true });
    return true;
  }

  restoreFocus(intent) {
    if (!intent.shouldRestoreFocus) return;
    if (operations().hasPending()) {
      document.__cartAfterIdleFocus = () => this.restoreFocus(intent);
      return;
    }
    const active = document.activeElement;
    if (
      active
      && active !== document.body
      && active !== document.documentElement
      && active.isConnected
    ) return;
    const surface = this.currentSurface(intent);
    if (!surface) return;

    if (intent.quantity > 0) {
      const row = findLineForIntent(surface, intent);
      const preferredSelector = { minus: '[data-quantity-minus]', input: '[data-quantity-value]' }[intent.action]
        || '[data-quantity-plus]';
      const preferred = row?.querySelector(preferredSelector);
      if (this.focusElement(preferred)) return;
      const fallback = row?.querySelector('[data-quantity-plus], [data-remove-item], .cart-line__title');
      if (this.focusElement(fallback)) return;
    } else {
      const adjacent = findLineByKey(surface, intent.nextLineKey)
        || findLineByKey(surface, intent.previousLineKey)
        || surface.querySelector('[data-cart-item]');
      const adjacentTarget = adjacent?.querySelector('.cart-line__title, [data-remove-item], [data-quantity-plus]');
      if (this.focusElement(adjacentTarget)) return;
    }

    if (intent.sourceKind === 'drawer') {
      this.focusElement(surface.querySelector('.cart-drawer__close'));
    } else {
      this.focusElement(surface.querySelector('.cart-empty__cta, [data-cart-checkout]'));
    }
  }

  cartMatchesIntent(data, intent) {
    if (!data || !Array.isArray(data.items)) return false;
    const row = findLineForIntent(this.currentSurface(intent), intent);
    const line = data.items.find((item) => item.key === (row?.dataset.lineKey || intent.lineKey));
    if (intent.quantity === 0) return !!intent.identity && !row && !line;
    return !!row && !!line && Number(line.quantity) === intent.quantity;
  }

  async fetchCart(url, body, operation) {
    document.dispatchEvent(new CustomEvent('cart:changing'));
    let data;
    try { data = await operation.requestJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }); } catch (error) {
      if (error.code === 'http') error.message = errorMessageFrom(error.data);
      throw error;
    }
    if (!isValidCartPayload(data)) throw new CartUpdateError(DEFAULT_MESSAGES.updateError, 'payload');
    return data;
  }

  async reconcile(intent, operation) {
    try {
      const config = this.getRequestConfig(intent);
      if (!intent.requestedSectionIds) intent.requestedSectionIds = config.sectionIds;
      const data = await this.fetchCart(config.updateUrl, {
        updates: {},
        sections: config.sectionIds.join(','),
        sections_url: config.sectionsUrl,
      }, operation);
      const result = this.applyUpdate(data, intent);
      return result.allApplied ? { data, result } : null;
    } catch (error) {
      console.error('[CartLineControls] cart reconciliation failed:', error);
      return null;
    }
  }

  /**
   * Refresh a cart surface without changing a line. The drawer uses this
   * after add-to-cart events and when it opens, so refreshes share exactly
   * the same validation, section matching, rebinding and event contract as
   * quantity changes.
   */
  refresh(sourceSurface = this.container.closest(SURFACE_SELECTOR)) {
    return operations().run((operation) => this.refreshWithinOperation(operation, sourceSurface), { mutation: false });
  }

  async refreshWithinOperation(operation, sourceSurface = this.container.closest(SURFACE_SELECTOR)) {
    const intent = {
      sourceKind: surfaceKind(sourceSurface), sourceSectionId: sourceSurface?.dataset.cartSectionId || null, sourceSurface,
    };
    if (!intent.sourceKind) throw new CartUpdateError(DEFAULT_MESSAGES.updateError, 'configuration');
    this.clearFeedback(intent);
    try {
      const config = this.getRequestConfig(intent);
      intent.requestedSectionIds = config.sectionIds;
      const data = await this.fetchCart(config.updateUrl, {
        updates: {}, sections: config.sectionIds.join(','), sections_url: config.sectionsUrl,
      }, operation);
      if (!operation.isCurrent()) throw new CartUpdateError(DEFAULT_MESSAGES.updateError, 'stale');
      if (!this.applyUpdate(data, intent).allApplied) throw new CartUpdateError(DEFAULT_MESSAGES.updateError, 'render');
      return data;
    } catch (error) {
      this.showError(intent, this.messages(intent).updateError);
      throw error;
    }
  }

  /**
   * Change one cart line using bundled section rendering. Shopify can apply a
   * capped quantity while still returning 422, so every failed/ambiguous
   * response gets one truth-reconciliation attempt before feedback is shown.
   */
  async update(lineKey, quantity, lineEl, trigger, action) {
    if (cartLineMutationPending || operations().isBlocked()) return false;
    cartLineMutationPending = true;
    document.__cartQuantityDrafts?.delete(lineKey);
    const inputs = Array.from(document.querySelectorAll('input[data-quantity-value]'));
    inputs.forEach((input) => { input.readOnly = true; });
    lineEl.classList.add('is-updating');
    lineEl.setAttribute('aria-busy', 'true');
    this.container.setAttribute('aria-busy', 'true');
    try {
      return await operations().run(
        (operation) => this.performUpdate(lineKey, quantity, lineEl, trigger, action, operation),
      );
    } finally {
      if (lineEl.isConnected) {
        lineEl.classList.remove('is-updating');
        lineEl.removeAttribute('aria-busy');
      }
      if (this.container.isConnected) this.container.removeAttribute('aria-busy');
      lockQuantityControls();
      if (trigger?.isConnected && action === 'input') trigger.value = trigger.defaultValue;
      cartLineMutationPending = false;
    }
  }

  async performUpdate(lineKey, quantity, lineEl, trigger, action, operation) {
    const intent = this.captureIntent(lineKey, quantity, lineEl, trigger, action);
    if (!intent.sourceKind) return;
    lineEl.classList.add('is-updating');
    lineEl.setAttribute('aria-busy', 'true');
    this.clearFeedback(intent);
    let reconciliationAttempted = false;

    try {
      const config = this.getRequestConfig(intent);
      intent.requestedSectionIds = config.sectionIds;
      const data = await this.fetchCart(config.changeUrl, {
        id: lineKey,
        quantity,
        sections: config.sectionIds.join(','),
        sections_url: config.sectionsUrl,
      }, operation);
      this.captureFocusOwnership(intent);
      let result = this.applyUpdate(data, intent);
      if (!result.allApplied) {
        reconciliationAttempted = true;
        const reconciled = await this.reconcile(intent, operation);
        if (!reconciled) throw new CartUpdateError(this.messages(intent).updateError, 'render');
        result = reconciled.result;
      }
      if (!result.allApplied) throw new CartUpdateError(this.messages(intent).updateError, 'render');
      this.announceSuccess(intent);
      this.restoreFocus(intent);
      return true;
    } catch (error) {
      this.captureFocusOwnership(intent);
      let reconciled = null;
      if (!reconciliationAttempted) {
        reconciliationAttempted = true;
        reconciled = await this.reconcile(intent, operation);
      }

      if (!reconciled) operation.markUncertain();

      // A fresh snapshot updates presentation; an ambiguous write remains blocked.
      if (!operations().isBlocked() && error?.code !== 'http' && reconciled && this.cartMatchesIntent(reconciled.data, intent)) {
        this.announceSuccess(intent);
      } else {
        console.error('[CartLineControls] cart update failed:', error);
        const message = error?.code === 'http'
          ? error.message
          : this.messages(intent).updateError;
        this.showError(intent, message);
      }
      this.restoreFocus(intent);
      return !operations().isBlocked() && error?.code !== 'http' && !!reconciled && this.cartMatchesIntent(reconciled.data, intent);
    } finally {
      if (lineEl.isConnected) {
        lineEl.classList.remove('is-updating');
        lineEl.removeAttribute('aria-busy');
      }
    }
  }

  captureSurfaceFocus(surface, intent) {
    const active = document.activeElement;
    if (!surface?.contains(active)) return null;
    const focusedLine = active?.closest('[data-cart-item]');
    if (focusedLine) {
      if (intent?.shouldRestoreFocus && focusedLine === intent.lineEl) return null;
      const rowMarker = active.closest(
        '[data-quantity-minus], [data-quantity-plus], [data-quantity-value], [data-remove-item], .cart-line__title',
      );
      if (!rowMarker) return null;
      const rows = Array.from(surface.querySelectorAll('[data-cart-item]'));
      const selectors = [
        '[data-quantity-minus]',
        '[data-quantity-plus]',
        '[data-quantity-value]',
        '[data-remove-item]',
        '.cart-line__title',
      ];
      return {
        element: rowMarker,
        selector: selectors.find((selector) => rowMarker.matches(selector)),
        lineKey: focusedLine.dataset.lineKey || null,
        variantId: focusedLine.dataset.variantId || null,
        identity: focusedLine.dataset.lineIdentity || null,
        rowIndex: rows.indexOf(focusedLine),
      };
    }
    const marker = active.closest(
      '[data-cart-note-input], [data-cart-checkout], [data-cart-share], [data-checkout-btn], .cart-summary__continue',
    );
    if (!marker) return null;
    const selectors = [
      '[data-cart-note-input]',
      '[data-cart-checkout]',
      '[data-cart-share]',
      '[data-checkout-btn]',
      '.cart-summary__continue',
    ];
    return {
      element: marker,
      selector: selectors.find((selector) => marker.matches(selector)),
    };
  }

  restoreSurfaceFocus(snapshot, surface) {
    if (snapshot && operations().hasPending()) {
      document.__cartAfterIdleFocus = () => this.restoreSurfaceFocus(snapshot, surface);
      return;
    }
    if (!snapshot) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    if (this.focusElement(snapshot.element)) return;
    if (snapshot.lineKey || snapshot.variantId) {
      const line = findLineForIntent(surface, snapshot);
      if (this.focusElement(line?.querySelector(snapshot.selector))) return;
    }
    this.focusElement(surface?.querySelector(snapshot.selector));
  }

  buildUpdatePlan(parsedSection, liveSection, data) {
    const kind = surfaceKind(parsedSection);
    if (kind === 'page' && data.item_count === 0) {
      return { kind, liveSection, parsedSection, replaceWhole: true };
    }

    const liveItems = liveSection.querySelector('[data-cart-items]');
    const freshItems = parsedSection.querySelector('[data-cart-items]');
    if (kind === 'page' && !liveItems && freshItems) {
      const hasCompleteFreshPage = parsedSection.querySelector('[data-cart-page-header]')
        && parsedSection.querySelector('[data-cart-summary]')
        && parsedSection.querySelector('[data-cart-sticky-bar]');
      return hasCompleteFreshPage
        ? { kind, liveSection, parsedSection, replaceWhole: true }
        : null;
    }
    if (!liveItems || !freshItems) return null;
    if (kind === 'drawer') {
      const liveCount = liveSection.querySelector('[data-cart-count]');
      const freshCount = parsedSection.querySelector('[data-cart-count]');
      const liveSummary = liveSection.querySelector('[data-cart-drawer-summary]');
      const freshSummary = parsedSection.querySelector('[data-cart-drawer-summary]');
      const liveFooter = liveSection.querySelector('[data-cart-footer]');
      const freshFooter = parsedSection.querySelector('[data-cart-footer]');
      const liveNote = liveSection.querySelector('[data-cart-note]');
      const freshNote = parsedSection.querySelector('[data-cart-note]');
      if (!liveCount || !freshCount || !liveSummary || !freshSummary || !liveFooter || !freshFooter) return null;
      if (!!liveNote !== !!freshNote) return null;
      return {
        kind,
        liveSection,
        parsedSection,
        liveItems,
        freshItems,
        liveCount,
        freshCount,
        liveSummary,
        freshSummary,
        liveFooter,
        freshFooter,
        liveNote,
      };
    }

    const liveHeader = liveSection.querySelector('[data-cart-page-header]');
    const freshHeader = parsedSection.querySelector('[data-cart-page-header]');
    const liveSummary = liveSection.querySelector('[data-cart-summary]');
    const freshSummary = parsedSection.querySelector('[data-cart-summary]');
    const liveSticky = liveSection.querySelector('[data-cart-sticky-bar]');
    const freshSticky = parsedSection.querySelector('[data-cart-sticky-bar]');
    if (!liveHeader || !freshHeader || !liveSummary || !freshSummary || !liveSticky || !freshSticky) return null;

    const liveNote = liveSummary.querySelector('[data-cart-note]');
    const freshNote = freshSummary.querySelector('[data-cart-note]');
    if (!!liveNote !== !!freshNote) return null;
    return {
      kind,
      liveSection,
      parsedSection,
      liveItems,
      freshItems,
      liveHeader,
      freshHeader,
      liveSummary,
      freshSummary,
      liveSticky,
      freshSticky,
      liveNote,
      freshNote,
    };
  }

  applyUpdate(data, intent) {
    if (!isValidCartPayload(data)) {
      return {
        sourceApplied: false,
        allApplied: false,
        appliedKinds: new Set(),
        appliedSectionIds: new Set(),
      };
    }
    const plans = [];
    Object.values(data.sections).forEach((html) => {
      if (typeof html !== 'string' || !html.trim()) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const parsedSection = parsed.body.querySelector(SURFACE_SELECTOR);
      if (!parsedSection) return;
      const kind = surfaceKind(parsedSection);
      const selector = kind === 'page' ? '[data-cart-page]' : '[data-cart-drawer]';
      const candidates = Array.from(document.querySelectorAll(selector));
      const sectionId = parsedSection.dataset.cartSectionId;
      const liveSection = sectionId
        ? candidates.find((surface) => surface.dataset.cartSectionId === sectionId)
        : null;
      if (!liveSection) return;
      const plan = this.buildUpdatePlan(parsedSection, liveSection, data);
      if (plan) plans.push({ ...plan, sectionId });
    });

    const requiredSectionIds = intent.requestedSectionIds || [intent.sourceSectionId].filter(Boolean);
    const plannedSectionIds = new Set(plans.map((plan) => plan.sectionId));
    const allSectionsPlanned = requiredSectionIds.length > 0
      && requiredSectionIds.every((sectionId) => plannedSectionIds.has(sectionId));
    if (!allSectionsPlanned) {
      return {
        sourceApplied: false,
        allApplied: false,
        appliedKinds: new Set(),
        appliedSectionIds: new Set(),
      };
    }

    const appliedKinds = new Set();
    const appliedSectionIds = new Set();
    plans.forEach((plan) => {
      const focusSnapshot = this.captureSurfaceFocus(plan.liveSection, intent);
      if (plan.replaceWhole) {
        plan.liveSection.replaceWith(plan.parsedSection);
        appliedKinds.add(plan.kind);
        appliedSectionIds.add(plan.sectionId);
        this.restoreSurfaceFocus(focusSnapshot, plan.parsedSection);
        return;
      }

      const liveHint = plan.liveSection.querySelector('[data-cart-shipping-hint]');
      const freshHint = plan.parsedSection.querySelector('[data-cart-shipping-hint]');
      if (liveHint && freshHint) liveHint.replaceWith(freshHint);
      plan.liveItems.replaceWith(plan.freshItems);
      if (plan.kind === 'page') {
        if (plan.liveNote && plan.freshNote) plan.freshNote.replaceWith(plan.liveNote);
        const trust = plan.liveSummary.querySelector('[data-cart-trust]');
        const freshTrust = plan.freshSummary.querySelector('[data-cart-trust]');
        if (trust && freshTrust) freshTrust.replaceWith(trust);
        plan.liveHeader.replaceWith(plan.freshHeader);
        plan.liveSummary.replaceWith(plan.freshSummary);
        plan.liveSticky.replaceWith(plan.freshSticky);
      } else {
        const liveEmpty = plan.liveSection.querySelector('[data-cart-empty]');
        const targetItems = plan.liveSection.querySelector('[data-cart-items]');
        plan.liveCount.textContent = plan.freshCount.textContent;
        plan.liveSummary.replaceWith(plan.freshSummary);
        plan.liveFooter.style.display = data.item_count === 0 ? 'none' : '';
        if (plan.liveNote) plan.liveNote.style.display = data.item_count === 0 ? 'none' : '';
        if (liveEmpty) {
          if (data.item_count === 0) {
            if (targetItems) targetItems.style.display = 'none';
            liveEmpty.style.display = '';
          } else {
            if (targetItems) targetItems.style.display = '';
            liveEmpty.style.display = 'none';
          }
        }
      }
      appliedKinds.add(plan.kind);
      appliedSectionIds.add(plan.sectionId);
      this.restoreSurfaceFocus(focusSnapshot, plan.liveSection);
    });

    document.querySelectorAll('[data-cart-items]').forEach((container) => {
      new CartLineControls(container);
    });
    Array.from(document.__cartQuantityDrafts || []).forEach(([key, draft]) => {
      let mappedKey = null;
      let stillDirty = false;
      document.querySelectorAll(SURFACE_SELECTOR).forEach((surface) => {
        const row = findLineForIntent(surface, { lineKey: key, identity: draft.identity });
        const input = row?.querySelector('[data-quantity-value]');
        if (input) {
          input.value = draft.value;
          mappedKey = row.dataset.lineKey;
          stillDirty = input.value !== input.defaultValue;
        } else operations().markUncertain();
      });
      if (mappedKey) {
        document.__cartQuantityDrafts.delete(key);
        if (stillDirty) document.__cartQuantityDrafts.set(mappedKey, draft);
      }
    });
    lockQuantityControls();
    const sourceApplied = appliedKinds.has(intent.sourceKind);
    const allApplied = requiredSectionIds.every((sectionId) => appliedSectionIds.has(sectionId));
    if (sourceApplied && allApplied) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    }
    return {
      sourceApplied,
      allApplied,
      appliedKinds,
      appliedSectionIds,
    };
  }
}

function autoInit() {
  document.querySelectorAll('[data-cart-items]').forEach((c) => {
    new CartLineControls(c);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}

// Expose on window so non-module consumers (cart-drawer.liquid inline
// IIFE that runs without an `import`) can re-bind freshly-inserted
// [data-cart-items] containers after their own server fetch + swap.
if (typeof window !== 'undefined') {
  window.CartLineControls = CartLineControls;
}
